<template>
	<base-modal
		ref="$modal"
		:title="modalTitle"
		:loading="isLoading"
		width="640px"
		height="80vh"
	>
		<div class="cp-info">
			<template v-if="!isLoading && info">
				<div class="cp-info__line">
					<span class="cp-info__label">Дата создания:</span>
					<span class="cp-info__value">
						{{ formatShortDate(info.Date) }}<template v-if="formatShortDate(info.Date)">,</template>
						{{ info.CreatedInCrm ? 'создан в CRM' : 'создан в 1С' }}
					</span>
				</div>
				<div
					v-if="info.Creator?.Name"
					class="cp-info__line"
				>
					<span class="cp-info__label">Создатель:</span>
					<span class="cp-info__value">{{ info.Creator.Name }}</span>
				</div>

				<div
					v-for="row in requisites"
					:key="row.label"
					class="cp-info__line"
				>
					<span class="cp-info__label">{{ row.label }}:</span>
					<span class="cp-info__value">{{ row.value }}</span>
				</div>

				<div class="cp-info__line">
					<span class="cp-info__label">Экземпляр 1С:</span>
					<span class="cp-info__value">{{ info.Instance1C || 'По умолчанию' }}</span>
				</div>

				<!-- Привязан к клиенту + перепривязка -->
				<div class="cp-info__client">
					<div class="cp-info__client-head">
						<span class="cp-info__label">
							{{ info.Client ? 'Привязан к клиенту:' : 'Не привязан к клиенту:' }}
						</span>
						<app-resizable-dropdown
							ref="$reattachDropdown"
							position="right"
							resizable
							:initial-width="360"
							:initial-height="420"
							:min-width="320"
							:max-width="600"
							:min-height="300"
							:max-height="700"
							:content-z-index="10010"
							:close-by-click-outside="true"
						>
							<template #activator>
								<app-tooltip text="Сменить привязанного клиента">
									<template #activator>
										<app-icon
											class="cp-info__client-edit"
											name="edit-pencil"
											width="14px"
											color="#979797"
											hover-color="#499259"
											cursor="pointer"
										/>
									</template>
								</app-tooltip>
							</template>
							<client-reattach-dropdown
								:counterparty-id="currentId"
								:current-client-id="info.Client?.Id"
								show-close-icon
								@reattached="onReattached"
								@close="$reattachDropdown?.close()"
							/>
						</app-resizable-dropdown>
					</div>

					<div
						v-if="info.Client"
						class="cp-info__client-card"
					>
						<router-link
							class="cp-info__client-name"
							:to="{ query: { page: 'client', id: info.Client.Id } }"
							@click="onModalClose"
						>
							{{ info.Client.Name }}
						</router-link>
						<span
							v-if="info.Client.AddressChain?.length"
							class="cp-info__client-address"
						>({{ info.Client.AddressChain.join(', ') }})</span>
						<span
							v-if="info.Client.Tags?.length"
							class="cp-info__client-tags"
						>
							<span
								v-for="tag in info.Client.Tags"
								:key="tag.Id"
								class="cp-info__client-tag"
							>#{{ tag.Name }}</span>
						</span>
					</div>
				</div>

				<!-- Счета -->
				<div
					v-if="outgoingBills.length"
					class="cp-info__bills"
				>
					<div class="cp-info__label">Исходящие счета:</div>
					<div
						v-for="(bill, i) in outgoingBills"
						:key="bill.Id"
						class="cp-info__bill"
					>
						<span class="cp-info__bill-index">{{ i + 1 }}.</span>
						<span class="cp-info__bill-text">
							<app-icon
								class="cp-info__bill-info"
								name="info-circle-outline"
								width="14px"
								color="#979797"
								hover-color="#499259"
								cursor="pointer"
								title="Подробная информация о счёте"
								@click="openBillInfo(bill)"
							/>
							Сч. исх. <span class="cp-info__green">{{ bill.Number || '—' }}</span>
							[{{ bill.Instance1C || 'По умолчанию' }}]
							<span class="cp-info__green">от {{ formatShortDate(bill.Date) }},</span>
							<span class="cp-info__bill-cp">{{ bill.Counterparty?.Name || '[без КА]' }}</span>
							<span class="cp-info__green"> | {{ (bill.TotalAmount ?? 0).toLocaleString('ru-RU') }},</span>
							<span class="cp-info__bar">
								<span
									class="cp-info__bar-fill"
									:style="{ width: barWidth(bill.PayedPercent) }"
								/>
							</span>
							<span
								v-if="bill.HasPayments"
								class="cp-info__green"
							>{{ bill.PayedPercent ?? 0 }}% ({{ (bill.PayedAmount ?? 0).toLocaleString('ru-RU') }})</span>
							<span v-else>{{ payStatusText(bill.PayStatus) }}</span>
							<span
								v-if="bill.Note"
								class="cp-info__bill-note"
							>[{{ bill.Note }}]</span>
						</span>
					</div>
				</div>

				<div
					v-if="incomingBills.length"
					class="cp-info__bills"
				>
					<div class="cp-info__label">Входящие счета:</div>
					<div
						v-for="(bill, i) in incomingBills"
						:key="bill.Id"
						class="cp-info__bill"
					>
						<span class="cp-info__bill-index">{{ i + 1 }}.</span>
						<span class="cp-info__bill-text">
							<app-icon
								class="cp-info__bill-info"
								name="info-circle-outline"
								width="14px"
								color="#979797"
								hover-color="#499259"
								cursor="pointer"
								title="Подробная информация о счёте"
								@click="openBillInfo(bill)"
							/>
							Сч. вх. <span class="cp-info__green">{{ bill.Number || '—' }}</span>
							[{{ bill.Instance1C || 'По умолчанию' }}]
							<span class="cp-info__green">от {{ formatShortDate(bill.Date) }},</span>
							<span class="cp-info__bill-cp">{{ bill.Counterparty?.Name || '[без КА]' }}</span>
							<span class="cp-info__green"> | {{ (bill.TotalAmount ?? 0).toLocaleString('ru-RU') }},</span>
							<span class="cp-info__bar">
								<span
									class="cp-info__bar-fill"
									:style="{ width: barWidth(bill.PayedPercent) }"
								/>
							</span>
							<span
								v-if="bill.HasPayments"
								class="cp-info__green"
							>{{ bill.PayedPercent ?? 0 }}% ({{ (bill.PayedAmount ?? 0).toLocaleString('ru-RU') }})</span>
							<span v-else>{{ payStatusText(bill.PayStatus) }}</span>
							<span
								v-if="bill.Note"
								class="cp-info__bill-note"
							>[{{ bill.Note }}]</span>
						</span>
					</div>
				</div>
			</template>

			<div
				v-else-if="!isLoading && !info"
				class="cp-info__empty"
			>
				Не удалось загрузить данные контрагента
			</div>
		</div>

		<bill-info-modal
			ref="$billInfoModal"
			@changed="onBillChanged"
		/>
	</base-modal>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import BaseModal from "@/components/common/modal/BaseModal.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import AppResizableDropdown from "@/components/ui-kit/dropdown/AppResizableDropdown.vue";
import ClientReattachDropdown from "@/components/main/client-block-page/dropdowns/ClientReattachDropdown.vue";
import { useQuasar } from "quasar";
import { financesStore } from "@/store/finances";
import BillInfoModal from "./BillInfoModal.vue";
import BillInfoDialog from "@/components/multiedit/temporary/storageshared/BillInfoDialog.vue";
import { CounterpartyInfo } from "@/models/interfaces/finances/counterparty-models";
import { BillWithOneLink } from "@/models/interfaces/finances/finance-models";
import { BillType, BillLinkedTypeDescriptor } from "@/models/etc/types";
import { formatShortDate, payStatusText, barWidth } from "./bill-format";

const info = ref<CounterpartyInfo | null>(null)
const isLoading = ref(true)
const currentId = ref(0)

const $modal = ref<InstanceType<typeof BaseModal> | null>(null)
const $reattachDropdown = ref<InstanceType<typeof AppResizableDropdown> | null>(null)
const $billInfoModal = ref<InstanceType<typeof BillInfoModal> | null>(null)
const $q = useQuasar()

const modalTitle = computed(() => {
	const name = info.value?.Name || ''
	const base = `Полная информация по контрагенту «${name}»`
	return info.value?.IsDraft ? `${base} [Черновик]` : base
})

const requisites = computed(() => {
	const m = info.value
	if (!m) return []
	return [
		{ label: 'ИНН', value: m.TaxpayerNumber },
		{ label: 'КПП', value: m.ReasonCode },
		{ label: 'Лицевой счет', value: m.PersonalAccount },
		{ label: 'Банковский счет или расчетный счет', value: m.BankAccount },
		{ label: 'Название банка', value: m.BankName },
		{ label: 'БИК', value: m.BankIdCode },
		{ label: 'Корреспондентский счет банка', value: m.CorrespondentBankAccount },
		{ label: 'Юридический адрес', value: m.LegalAddress },
		{ label: 'Правовая форма', value: m.LegalForm },
		{ label: 'Комментарий', value: m.Note },
	].filter(row => row.value != null && String(row.value).trim() !== '')
})

const outgoingBills = computed(() =>
	(info.value?.Bills ?? []).filter(b => b.Type === BillType.Outgoing)
)
const incomingBills = computed(() =>
	(info.value?.Bills ?? []).filter(b => b.Type === BillType.Incoming)
)

const onOpen = async (counterpartyId: number) => {
	info.value = null
	currentId.value = counterpartyId
	isLoading.value = true
	$modal.value?.onOpen()
	try {
		info.value = (await financesStore().getInfoCounterparty(counterpartyId)) ?? null
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const onReattached = async () => {
	info.value = (await financesStore().getInfoCounterparty(currentId.value)) ?? info.value
	$reattachDropdown.value?.close()
}

const openBillInfo = (bill: BillWithOneLink) => {
	// платёж (Descriptor===Payment) — открываем легаси-модалку, счёт — редизайн-модалку
	if (bill.Descriptor === BillLinkedTypeDescriptor.Payment) {
		$q.dialog({
			component: BillInfoDialog,
			componentProps: { existingBillId: bill.Id, isPayment: true }
		})
		return
	}
	$billInfoModal.value?.onOpen(bill.Id)
}

const onBillChanged = async () => {
	info.value = (await financesStore().getInfoCounterparty(currentId.value)) ?? info.value
}

const onModalClose = () => $modal.value?.onClose()

defineExpose({ onOpen, onClose: onModalClose })
</script>

<style scoped lang="scss">
.cp-info {
	display: flex;
	flex-direction: column;
	gap: 4px;
	padding: 12px;

	&__line {
		font-size: 14px;
		line-height: 18px;
	}

	&__label {
		font-weight: 500;
		color: #4D4D4D;
	}

	&__value {
		margin-left: 6px;
		color: #333;
		word-break: break-word;
	}

	&__green {
		color: #499259;
	}

	&__client {
		display: flex;
		flex-direction: column;
		gap: 4px;
		margin-top: 4px;

		&-head {
			display: flex;
			align-items: center;
			gap: 6px;
		}

		&-edit {
			margin-top: -2px;
		}

		&-card {
			font-size: 14px;
			line-height: 18px;
		}

		&-name {
			font-weight: 500;
			color: #438451;
			text-decoration: none;

			&:hover {
				text-decoration: underline;
			}
		}

		&-address {
			margin-left: 6px;
			color: #979797;
		}

		&-tags {
			margin-left: 6px;
		}

		&-tag {
			margin-right: 6px;
			color: #979797;
		}
	}

	&__bills {
		display: flex;
		flex-direction: column;
		gap: 4px;
		margin-top: 4px;
	}

	&__label + &__bill,
	&__bills > &__label {
		font-weight: 500;
		color: #4D4D4D;
	}

	&__bill {
		display: flex;
		align-items: baseline;
		gap: 4px;
		font-size: 13px;
		line-height: 18px;
		color: #4D4D4D;

		&-index {
			flex-shrink: 0;
		}

		&-info {
			margin-top: -2px;
		}

		&-text {
			min-width: 0;
			word-break: break-word;
		}

		&-cp {
			font-weight: 600;
		}

		&-note {
			color: #979797;
		}
	}

	&__bar {
		display: inline-block;
		vertical-align: middle;
		width: 64px;
		height: 6px;
		margin: 0 4px;
		border-radius: 3px;
		background: #EDEDED;
		overflow: hidden;

		&-fill {
			display: block;
			height: 100%;
			border-radius: 3px;
			background: #499259;
			transition: width 0.2s ease;
		}
	}

	&__empty {
		font-size: 14px;
		line-height: 18px;
		color: #979797;
		padding: 12px 0;
	}
}
</style>
